import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Card from '../../components/Card.jsx';
import Button from '../../components/Button.jsx';
import { useAuth } from '../../context/AuthContext.jsx';
import apiService from '../../context/apiService.js';
import { toast } from 'react-toastify';
import { 
  BookOpen, 
  Clock, 
  Star,
  CheckCircle,
  TrendingUp,
  Loader2,
  Search,
  Filter,
  Calendar,
  Award,
  BarChart3,
  Play
} from 'lucide-react';

const StudentEnrollments = () => {
  const { userData } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [lessonCounts, setLessonCounts] = useState({});

  // Fetch lesson counts for courses
  const fetchLessonCounts = async (enrollmentsData) => {
    const counts = {};
    for (const enrollment of enrollmentsData) {
      const course = enrollment.course || enrollment;
      try {
        const response = await apiService.lessons.getByCourse(course._id);
        counts[course._id] = response?.data?.count || 0;
      } catch (error) {
        counts[course._id] = 0;
      }
    }
    setLessonCounts(counts);
  };

  // Fetch enrolled courses with detailed information
  const fetchEnrollments = async () => {
    try {
      const response = await apiService.enrollment.getEnrolledCourses();
      if (response.data.success) {
        const enrollmentData = response.data.enrollments || response.data.courses;
        setEnrollments(enrollmentData);
        
        // Fetch lesson counts
        if (enrollmentData.length > 0) {
          await fetchLessonCounts(enrollmentData);
        }
      }
    } catch (error) {
      console.error('Error fetching enrollments:', error);
      toast.error('Failed to load enrollments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userData && userData.role === 'student') {
      fetchEnrollments();
    }
  }, [userData]);

  // Filter and sort enrollments
  const filteredEnrollments = enrollments.filter(enrollment => {
    const course = enrollment.course || enrollment;
    const matchesSearch = course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         course.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         course.creater?.name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    let matchesStatus = true;
    if (statusFilter !== 'all') {
      const progress = enrollment.progress || 0;
      if (statusFilter === 'completed') {
        matchesStatus = progress === 100;
      } else if (statusFilter === 'in-progress') {
        matchesStatus = progress > 0 && progress < 100;
      } else if (statusFilter === 'not-started') {
        matchesStatus = progress === 0;
      }
    }
    
    return matchesSearch && matchesStatus;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'progress':
        return (b.progress || 0) - (a.progress || 0);
      case 'title':
        const courseA = a.course || a;
        const courseB = b.course || b;
        return courseA.title.localeCompare(courseB.title);
      case 'recent':
      default:
        return new Date(b.enrolledAt || b.createdAt) - new Date(a.enrolledAt || a.createdAt);
    }
  });

  // Calculate overall stats
  const stats = {
    total: enrollments.length,
    completed: enrollments.filter(e => (e.progress || 0) === 100).length,
    inProgress: enrollments.filter(e => (e.progress || 0) > 0 && (e.progress || 0) < 100).length,
    notStarted: enrollments.filter(e => (e.progress || 0) === 0).length,
    avgProgress: enrollments.length > 0 
      ? Math.round(enrollments.reduce((sum, e) => sum + (e.progress || 0), 0) / enrollments.length)
      : 0
  };

  // Format enrollment date
  const formatEnrollmentDate = (enrollment) => {
    const enrolledAt = enrollment.enrolledAt || enrollment.createdAt || enrollment.updatedAt;
    if (!enrolledAt) return 'Recently';
    
    const date = new Date(enrolledAt);
    if (isNaN(date.getTime())) return 'Recently';
    
    const now = new Date();
    const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get status badge color
  const getStatusBadgeColor = (progress) => {
    if (progress === 100) return 'bg-green-100 text-green-700';
    if (progress > 0) return 'bg-blue-100 text-blue-700';
    return 'bg-gray-100 text-gray-700';
  };

  // Get status text
  const getStatusText = (progress) => {
    if (progress === 100) return 'Completed';
    if (progress > 0) return 'In Progress';
    return 'Not Started';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center space-x-2">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>Loading enrollments...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">My Enrollments</h1>
          <p className="text-secondary-600 mt-1">Track your learning progress and manage your enrolled courses</p>
        </div>
        <Button 
          variant="outline" 
          onClick={fetchEnrollments}
          disabled={loading}
          className="flex items-center space-x-2"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : null}
          <span>Refresh</span>
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card padding="sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-secondary-600">Total Enrolled</p>
              <p className="text-2xl font-bold text-secondary-900">{stats.total}</p>
            </div>
            <div className="p-3 rounded-lg bg-blue-50 text-blue-600">
              <BookOpen size={24} />
            </div>
          </div>
        </Card>

        <Card padding="sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-secondary-600">Completed</p>
              <p className="text-2xl font-bold text-secondary-900">{stats.completed}</p>
            </div>
            <div className="p-3 rounded-lg bg-green-50 text-green-600">
              <CheckCircle size={24} />
            </div>
          </div>
        </Card>

        <Card padding="sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-secondary-600">In Progress</p>
              <p className="text-2xl font-bold text-secondary-900">{stats.inProgress}</p>
            </div>
            <div className="p-3 rounded-lg bg-yellow-50 text-yellow-600">
              <Clock size={24} />
            </div>
          </div>
        </Card>

        <Card padding="sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-secondary-600">Not Started</p>
              <p className="text-2xl font-bold text-secondary-900">{stats.notStarted}</p>
            </div>
            <div className="p-3 rounded-lg bg-gray-50 text-gray-600">
              <TrendingUp size={24} />
            </div>
          </div>
        </Card>

        <Card padding="sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-secondary-600">Avg Progress</p>
              <p className="text-2xl font-bold text-secondary-900">{stats.avgProgress}%</p>
            </div>
            <div className="p-3 rounded-lg bg-purple-50 text-purple-600">
              <BarChart3 size={24} />
            </div>
          </div>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card padding="sm">
        <div className="space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" size={20} />
            <input
              type="text"
              placeholder="Search your enrolled courses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input pl-10 w-full"
            />
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center space-x-2">
              <Filter size={16} className="text-secondary-600" />
              <span className="text-sm font-medium text-secondary-700">Filters:</span>
            </div>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input w-48"
            >
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="in-progress">In Progress</option>
              <option value="not-started">Not Started</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="input w-48"
            >
              <option value="recent">Most Recent</option>
              <option value="progress">Progress (High to Low)</option>
              <option value="title">Course Name (A-Z)</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-secondary-600">
          Showing {filteredEnrollments.length} of {enrollments.length} enrollments
        </p>
      </div>

      {/* Enrollments List */}
      {enrollments.length === 0 ? (
        <Card className="text-center py-12">
          <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No enrollments yet</h3>
          <p className="text-gray-600 mb-4">Start your learning journey by enrolling in a course.</p>
          <Link to="/student/courses">
            <Button>Browse Courses</Button>
          </Link>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredEnrollments.map((enrollment) => {
            const course = enrollment.course || enrollment;
            const progress = enrollment.progress || 0;
            
            return (
              <Card key={enrollment._id} className="overflow-hidden hover:shadow-medium transition-shadow">
                <div className="flex flex-col lg:flex-row">
                  {/* Course Thumbnail */}
                  <div className="lg:w-64 flex-shrink-0">
                    <div className="aspect-video bg-gray-200 relative">
                      <img 
                        src={course.thumbnail || 'https://via.placeholder.com/300x200?text=Course'} 
                        alt={course.title}
                        className="w-full h-full object-cover object-center"
                      />
                      <div className="absolute top-2 left-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(progress)}`}>
                          {getStatusText(progress)}
                        </span>
                      </div>
                      <div className="absolute top-2 right-2">
                        <span className="bg-white/90 backdrop-blur-sm px-2 py-1 text-xs font-medium rounded-full text-secondary-700 shadow-sm">
                          <BookOpen size={12} className="inline mr-1" />
                          {lessonCounts[course._id] !== undefined ? lessonCounts[course._id] : '...'} lessons
                        </span>
                      </div>
                      <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                        <Link to={`/student/courses/${course._id}`} className="opacity-0 hover:opacity-100 transition-opacity bg-white bg-opacity-90 rounded-full p-3">
                          <Play size={24} className="text-primary-600" />
                        </Link>
                      </div>
                    </div>
                  </div>

                  {/* Course Details */}
                  <div className="flex-1 p-6">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-secondary-900 mb-2">{course.title}</h3>
                        <p className="text-secondary-600 mb-2">by {course.creater?.name || 'Unknown Instructor'}</p>
                        <p className="text-sm text-secondary-600 mb-4 line-clamp-2">{course.description}</p>
                        
                        {/* Course Stats */}
                        <div className="flex items-center space-x-6 text-sm text-secondary-600 mb-4">
                          <div className="flex items-center space-x-1">
                            <Star size={16} className="text-yellow-500 fill-current" />
                            <span>{course.rating || '4.5'}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Calendar size={16} />
                            <span>Enrolled {formatEnrollmentDate(enrollment)}</span>
                          </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="mb-4">
                          <div className="flex items-center justify-between text-sm text-secondary-600 mb-2">
                            <span>Progress</span>
                            <span>{progress}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div 
                              className="bg-primary-600 h-3 rounded-full transition-all duration-300"
                              style={{ width: `${progress}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="lg:ml-6 lg:flex-shrink-0">
                        <div className="flex flex-col space-y-2 lg:w-48">
                          <Link to={`/student/courses/${course._id}`}>
                            <Button className="w-full">
                              {progress === 100 ? (
                                <>
                                  <Award size={16} className="mr-2" />
                                  View Certificate
                                </>
                              ) : (
                                <>
                                  <Play size={16} className="mr-2" />
                                  {progress === 0 ? 'Start Learning' : 'Continue Learning'}
                                </>
                              )}
                            </Button>
                          </Link>
                          
                          {progress > 0 && (
                            <div className="text-center">
                              <span className="text-sm text-secondary-600">
                                {progress === 100 ? 'Course Completed!' : `${100 - progress}% remaining`}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {filteredEnrollments.length === 0 && enrollments.length > 0 && (
        <Card className="text-center py-12">
          <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No enrollments found</h3>
          <p className="text-gray-600 mb-4">Try adjusting your search or filter criteria</p>
          <Button onClick={() => {
            setSearchQuery('');
            setStatusFilter('all');
          }}>
            Clear Filters
          </Button>
        </Card>
      )}
    </div>
  );
};

export default StudentEnrollments;
